import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { LogIn, User, Lock, Shield } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Login = () => {
  const { login } = useAuth();
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [userId, setUserId] = useState(null);
  const [tempUserData, setTempUserData] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, credentials);
      
      // Check if 2FA is required
      if (response.data.requires_2fa) {
        // User has 2FA enabled, show OTP modal
        setUserId(response.data.user_id);
        setTempUserData(response.data);
        setShow2FAModal(true);
        setLoading(false);
        toast.info('Ju lutem vendosni kodin 2FA');
      } else {
        // No 2FA, proceed with login
        localStorage.setItem('auth_token', response.data.token);
        localStorage.setItem('user_data', JSON.stringify(response.data.user));
        
        toast.success(response.data.message);
        login(response.data.user);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Gabim në login';
      toast.error(errorMessage);
      console.error('Login error:', error);
      setLoading(false);
    }
  };

  const handleOTPSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/otp/validate`, {
        user_id: userId,
        otp_code: otpCode
      });

      if (response.data.valid) {
        // OTP is valid, complete login
        localStorage.setItem('auth_token', tempUserData.token);
        localStorage.setItem('user_data', JSON.stringify(tempUserData.user));
        
        toast.success('Login me sukses me 2FA');
        login(tempUserData.user);
        setShow2FAModal(false);
      } else {
        toast.error('Kodi 2FA është i gabuar');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Gabim në verifikimin e 2FA';
      toast.error(errorMessage);
      console.error('2FA validation error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setCredentials(prev => ({ ...prev, [field]: value }));
  };



  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-slate-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-4">
          {/* Logo */}
          <div className="flex justify-center">
            <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-2xl">IT</span>
            </div>
          </div>
          
          <div>
            <CardTitle className="text-2xl font-bold text-slate-900">
              IT Solution
            </CardTitle>
            <CardDescription className="text-slate-600">
              Hyr në sistemin e menaxhimit të tiketave
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="username"
                  type="text"
                  value={credentials.username}
                  onChange={(e) => handleInputChange('username', e.target.value)}
                  placeholder="Shkruaj username-in"
                  className="pl-10"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  value={credentials.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  placeholder="Shkruaj password-in"
                  className="pl-10"
                  required
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-blue-600 hover:bg-blue-700"
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Po hyn...</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <LogIn className="w-4 h-4" />
                  <span>Hyr</span>
                </div>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* 2FA OTP Modal */}
      {show2FAModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                  <Shield className="w-8 h-8 text-blue-600" />
                </div>
              </div>
              <CardTitle>Verifikimi 2FA</CardTitle>
              <CardDescription>
                Vendosni kodin 6-shifror nga aplikacioni juaj 2FA
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleOTPSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="otp">Kodi 2FA</Label>
                  <Input
                    id="otp"
                    type="text"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="text-center text-2xl tracking-widest"
                    maxLength={6}
                    required
                    autoFocus
                  />
                </div>

                <div className="flex space-x-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      setShow2FAModal(false);
                      setOtpCode('');
                      setUserId(null);
                      setTempUserData(null);
                    }}
                  >
                    Anulo
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                    disabled={loading || otpCode.length !== 6}
                  >
                    {loading ? 'Po verifikon...' : 'Verifiko'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Login;